import React from "react";

// Source
import * as HelperSrc from "../../HelperSrc";
import StoreRoute from "../../store/Route";
import StoreRole from "../../store/Role";
import StoreLocalization from "../../store/Localization";
import StorePreference from "../../store/Preference";
import * as ModelPagination from "../../model/Pagination";
import * as ModelRoute from "../../model/Route";
import * as ModelCustomRoute from "../../model/custom/Route";
import * as ModelRole from "../../model/Role";
import * as ModelLocalization from "../../model/Localization";
import * as ModelCustomLocalization from "../../model/custom/Localization";
import * as ModelPreference from "../../model/Preference";
import * as ModelHelperSrc from "../../model/HelperSrc";
import * as ApiClient from "../../api/custom/Client";
import * as ModelClient from "../../model/custom/Client";
import ControllerAlert from "../Alert";

export default class ControllerClientList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelCustomRoute.Idata>;
    private storageRouteData = React.useRef<ModelCustomRoute.Idata | undefined>(undefined);
    private storeRole: ModelRole.Istore;
    private storageRoleData = React.useRef<ModelRole.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelCustomLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelCustomLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementUserName = React.useRef<HTMLInputElement | null>(null);
    private elementUsernameKana = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdate = React.useRef<HTMLInputElement | null>(null);
    private elementLatestUpdateById = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);
    private selectOption = React.useRef<ModelClient.IselectOption | undefined>(undefined);

    private isRendered = React.useRef<boolean>(false);
    private idRemove = React.useRef(0);
    private filter = React.useRef<ModelPagination.CustomPagination.IfilterClient | undefined>(undefined);
    private dataMainList = React.useRef<ModelClient.Idata[] | undefined>(undefined);
    private language = React.useRef<string>("");

    getStorageRole: ModelRole.Idata | undefined;

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelClient.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelClient.IselectOption | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getPagination: ModelPagination.Idata | undefined;
    setPagination: React.Dispatch<React.SetStateAction<ModelPagination.Idata | undefined>>;

    getFilter: ModelClient.Idata | undefined;
    setFilter: React.Dispatch<React.SetStateAction<ModelClient.Idata | undefined>>;

    getDataMainList: ModelClient.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelClient.Idata[] | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    getElementLatestUpdateByIdValue: string;
    setElementLatestUpdateByIdValue: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeRole = new StoreRole();
        this.storageRoleData.current = this.storeRole.storageGet();
        this.storeLocalization = new StoreLocalization();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getStorageRole] = React.useState<ModelRole.Idata | undefined>(this.storageRoleData.current);
        this.getStorageRole = getStorageRole;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelClient.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getPagination, setPagination] = React.useState<ModelPagination.Idata | undefined>(undefined);
        this.getPagination = getPagination;
        this.setPagination = setPagination;

        const [getFilter, setFilter] = React.useState<ModelClient.Idata | undefined>(undefined);
        this.getFilter = getFilter;
        this.setFilter = setFilter;

        const [getDataMainList, setDataMainList] = React.useState<ModelClient.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;

        const [getElementLatestUpdateByIdValue, setElementLatestUpdateByIdValue] = React.useState<string>("");
        this.getElementLatestUpdateByIdValue = getElementLatestUpdateByIdValue;
        this.setElementLatestUpdateByIdValue = setElementLatestUpdateByIdValue;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_client_list", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();

            this.apiDataList();
        }, []);

        React.useEffect(() => {
            this.storageRoleData.current = this.storeRole.storageGet();
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRouteData.current, this.storageRoleData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    apiDataList = (isFilter = false): void => {
        this.filterParameter(isFilter);

        if (this.filter.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                dataList: true
            }));

            ApiClient.dataList(this.filter.current, this.filter.current.sort_order, this.filter.current.page, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    dataList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.setPagination(response.pagination);

                this.dataMainList.current = response.pagination.data as unknown as ModelClient.Idata[];
                this.setDataMainList(this.dataMainList.current);

                this.controllerAlert.show("success", response.message.success);
            });
        }
    };

    clickCreate = (): void => {
        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.client_profile.path);
        }
    };

    clickFilterApply = (): void => {
        this.apiDataList(true);
    };

    clickItem = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, data: ModelClient.Idata): void => {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;

        if (this.storageRouteData.current && tagName !== "path") {
            HelperSrc.routerHistoryPush(this.storageRouteData.current.client_profile.path, data as unknown as Record<string, unknown>);
        }
    };

    clickRemove = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, id: number): void => {
        event.stopPropagation();

        this.idRemove.current = id;

        this.setIsDialogOpen(true);
    };

    clickDialogRemoveConfirm = (): void => {
        if (this.idRemove.current > 0) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                removeData: true
            }));

            ApiClient.removeData(this.idRemove.current, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    removeData: false
                }));

                this.setIsDialogOpen(false);

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.controllerAlert.show("success", response.message.success);

                this.apiDataList();
            });
        }
    };

    clickDialogRemoveCancel = (): void => {
        this.setIsDialogOpen(false);
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementUserName.current = document.querySelector<HTMLInputElement>("[name='username']");
            this.elementUsernameKana.current = document.querySelector<HTMLInputElement>("[name='username_kana']");
            this.elementLatestUpdate.current = document.querySelector<HTMLInputElement>("[name='latest_update']");
            this.elementLatestUpdateById.current = document.querySelector<HTMLInputElement>("[name='latest_update_by_id']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_client_list");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiClient.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.selectOption.current = response.data.result as unknown as ModelClient.IselectOption;

            this.setSelectOption(this.selectOption.current);
        });
    };

    private filterParameter = (isFilter: boolean): void => {
        const pageUrl = HelperSrc.urlVariable("page") && !isFilter ? HelperSrc.urlVariable("page") : "1";
        const sortOrderUlr = HelperSrc.urlVariable("sort_order") && !isFilter ? HelperSrc.urlVariable("sort_order") : "desc";
        const usernameUrl = HelperSrc.urlVariable("username") && !isFilter ? HelperSrc.urlVariable("username") : "";
        const usernameKanaUrl = HelperSrc.urlVariable("username_kana") && !isFilter ? HelperSrc.urlVariable("username_kana") : "";
        const latestUpdateUrl = HelperSrc.urlVariable("latest_update") && !isFilter ? HelperSrc.urlVariable("latest_update") : "";
        const latestUpdateByIdUrl = HelperSrc.urlVariable("latest_update_by_id") && !isFilter ? HelperSrc.urlVariable("latest_update_by_id") : "";

        const usernameResult =
            this.elementUserName.current && this.elementUserName.current.value !== usernameUrl ? this.elementUserName.current.value : usernameUrl;
        const usernameKanaResult =
            this.elementUsernameKana.current && this.elementUsernameKana.current.value !== usernameKanaUrl
                ? this.elementUsernameKana.current.value
                : usernameKanaUrl;
        const latestUpdateResult =
            this.elementLatestUpdate.current && this.elementLatestUpdate.current.value !== latestUpdateUrl
                ? this.elementLatestUpdate.current.value
                : latestUpdateUrl;
        const latestUpdateByIdResult =
            this.elementLatestUpdateById.current && this.elementLatestUpdateById.current.value !== latestUpdateByIdUrl
                ? this.elementLatestUpdateById.current.value
                : latestUpdateByIdUrl;

        this.filter.current = {
            page: pageUrl,
            sort_order: sortOrderUlr,
            username: usernameResult as string & ModelHelperSrc.IvalidationForm,
            username_kana: usernameKanaResult as string & ModelHelperSrc.IvalidationForm,
            latest_update: latestUpdateResult as unknown as string & ModelHelperSrc.IvalidationForm,
            latest_update_by_id: latestUpdateByIdResult as unknown as number & ModelHelperSrc.IvalidationForm
        } as ModelPagination.CustomPagination.IfilterClient;
        this.setFilter(this.filter.current);

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                `${this.storageRouteData.current.client_list.path}?page=${this.filter.current.page}&sort_order=${this.filter.current.sort_order}&` +
                    `username=${this.filter.current.username}&` +
                    `username_kana=${this.filter.current.username_kana}&` +
                    `latest_update=${this.filter.current.latest_update}&` +
                    `latest_update_by_id=${this.filter.current.latest_update_by_id}`,
                {},
                "",
                true
            );
        }
    };
}
